import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

import type { BlogPost } from '../../services/api';
import api from '../../services/api';

import styles from './BlogEditor.module.css';

/**
 * BlogEditor Props
 */
type BlogEditorProps = {
  readonly slug: string;
  readonly pageTitle: string;
  readonly initialContent: Record<string, unknown>;
  readonly onSave: (content: Record<string, unknown>) => Promise<void>;
  readonly isSaving: boolean;
};

/**
 * BlogEditor Component
 *
 * Admin editor for managing blog page content and posts.
 * Features:
 * - List of all posts (including drafts)
 * - Quick actions: edit, publish/unpublish, delete
 * - Reorder posts with up/down buttons
 * - Create new post button
 * - Blog page settings (postsPerPage, showExcerpts, etc.)
 */
const BlogEditor: React.FC<BlogEditorProps> = ({
  slug,
  pageTitle,
  initialContent,
  onSave,
  isSaving,
}) => {
  const [posts, setPosts] = useState<ReadonlyArray<BlogPost>>([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState<string>(
    (initialContent?.description as string) ?? ''
  );
  const [postsPerPage, setPostsPerPage] = useState<number>(
    ((initialContent?.settings as Record<string, unknown>)?.postsPerPage as number) ?? 10
  );
  const [showExcerpts, setShowExcerpts] = useState<boolean>(
    ((initialContent?.settings as Record<string, unknown>)?.showExcerpts as boolean) ?? true
  );
  const [showFeaturedImages, setShowFeaturedImages] = useState<boolean>(
    ((initialContent?.settings as Record<string, unknown>)?.showFeaturedImages as boolean) ?? true
  );
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Fetch posts for this blog page
   */
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedPosts = await api.blog.getAdminPosts(slug);
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // Initial fetch
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  /**
   * Save blog page settings
   */
  const handleSaveSettings = async () => {
    try {
      await onSave({
        description,
        settings: {
          postsPerPage,
          showExcerpts,
          showFeaturedImages,
        },
      });
      setHasChanges(false);
      toast.success('Settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  /**
   * Toggle publish status
   */
  const handleTogglePublish = async (post: BlogPost) => {
    try {
      const newStatus = post.status === 'published' ? false : true;
      await api.blog.publish(post._id, newStatus);
      await fetchPosts();
      toast.success(newStatus ? 'Post published' : 'Post unpublished');
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
      toast.error('Failed to update post status');
    }
  };

  /**
   * Delete post
   */
  const handleDelete = async (post: BlogPost) => {
    if (!window.confirm(`Are you sure you want to delete "${post.title}"?`)) {
      return;
    }

    try {
      await api.blog.delete(post._id);
      await fetchPosts();
      toast.success('Post deleted');
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast.error('Failed to delete post');
    }
  };

  /**
   * Move post up
   */
  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newPosts = [...posts];
    const temp = newPosts[index];
    newPosts[index] = newPosts[index - 1];
    newPosts[index - 1] = temp;

    try {
      await api.blog.reorder(newPosts.map((p) => p._id));
      await fetchPosts();
    } catch (error) {
      console.error('Failed to reorder posts:', error);
      toast.error('Failed to reorder posts');
    }
  };

  /**
   * Move post down
   */
  const handleMoveDown = async (index: number) => {
    if (index === posts.length - 1) return;

    const newPosts = [...posts];
    const temp = newPosts[index];
    newPosts[index] = newPosts[index + 1];
    newPosts[index + 1] = temp;

    try {
      await api.blog.reorder(newPosts.map((p) => p._id));
      await fetchPosts();
    } catch (error) {
      console.error('Failed to reorder posts:', error);
      toast.error('Failed to reorder posts');
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className={styles.blogEditor}>
      {/* Header */}
      <div className={styles.header}>
        <h2>{pageTitle}</h2>
        <Link to={`/admin/blog/${slug}/new`} className={styles.newPostButton}>
          + New Post
        </Link>
      </div>

      {/* Settings Section */}
      <div className={styles.settingsSection}>
        <h3>Blog Settings</h3>

        <div className={styles.settingsGrid}>
          <div className={styles.formGroup}>
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Optional blog description..."
              rows={3}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="postsPerPage">Posts Per Page</label>
            <input
              id="postsPerPage"
              type="number"
              min={1}
              max={50}
              value={postsPerPage}
              onChange={(e) => {
                setPostsPerPage(parseInt(e.target.value, 10) || 10);
                setHasChanges(true);
              }}
            />
          </div>

          <div className={styles.checkboxGroup}>
            <label>
              <input
                type="checkbox"
                checked={showExcerpts}
                onChange={(e) => {
                  setShowExcerpts(e.target.checked);
                  setHasChanges(true);
                }}
              />
              Show excerpts in post list
            </label>
          </div>

          <div className={styles.checkboxGroup}>
            <label>
              <input
                type="checkbox"
                checked={showFeaturedImages}
                onChange={(e) => {
                  setShowFeaturedImages(e.target.checked);
                  setHasChanges(true);
                }}
              />
              Show featured images in post list
            </label>
          </div>
        </div>

        {hasChanges && (
          <button
            className={styles.saveButton}
            onClick={handleSaveSettings}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </div>

      {/* Posts Section */}
      <div className={styles.postsSection}>
        <h3>Posts ({posts.length})</h3>

        {loading ? (
          <div className={styles.loading}>Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No posts yet.</p>
            <Link to={`/admin/blog/${slug}/new`} className={styles.createFirstPost}>
              Create your first post
            </Link>
          </div>
        ) : (
          <div className={styles.postsTable}>
            <div className={styles.tableHeader}>
              <span className={styles.colTitle}>Title</span>
              <span className={styles.colStatus}>Status</span>
              <span className={styles.colDate}>Date</span>
              <span className={styles.colActions}>Actions</span>
            </div>

            {posts.map((post, index) => (
              <div key={post._id} className={styles.postRow}>
                <span className={styles.colTitle}>
                  <Link to={`/admin/blog/${slug}/${post._id}`}>{post.title}</Link>
                </span>
                <span className={styles.colStatus}>
                  <span
                    className={`${styles.statusBadge} ${
                      post.status === 'published' ? styles.published : styles.draft
                    }`}
                  >
                    {post.status}
                  </span>
                </span>
                <span className={styles.colDate}>
                  {formatDate(post.publishedAt ?? post.createdAt)}
                </span>
                <span className={styles.colActions}>
                  <div className={styles.actionButtons}>
                    <button
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className={styles.actionButton}
                      title="Move up"
                    >
                      &uarr;
                    </button>
                    <button
                      onClick={() => handleMoveDown(index)}
                      disabled={index === posts.length - 1}
                      className={styles.actionButton}
                      title="Move down"
                    >
                      &darr;
                    </button>
                    <Link
                      to={`/admin/blog/${slug}/${post._id}`}
                      className={styles.actionButton}
                      title="Edit"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleTogglePublish(post)}
                      className={`${styles.actionButton} ${
                        post.status === 'published' ? styles.unpublishButton : styles.publishButton
                      }`}
                    >
                      {post.status === 'published' ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      onClick={() => handleDelete(post)}
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                    >
                      Delete
                    </button>
                  </div>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogEditor;
